<template>
  <!-- State: No Files | No Results | Error -->
  <div class="h-full flex justify-center" v-show="state">
    <slot name="state" />
  </div>

  <!-- Data: Table -->
  <DataGrid
    v-show="!state"
    :minLeftWidth="20"
    :maxLeftWidth="60"
    :leftWidth="leftWidth || 30"
    :showColumns="columns"
    class="bg-primary"
    @update:leftWidth="(value) => setPreference('files.grid.leftWidth', value)"
  >
    <template #left>
      <Table
        :isSelectable="isSelectable"
        :showColumns="columns"
        :data="files"
        :isLoading="isLoading"
        :sortBy="filters.sort_by"
        :orderBy="filters.order_by"
        :hasColumnPicker="false"
        :hoveredRow="hoveredRow"
        rowHeight="56"
        type="files"
        @clicked:row="handleClickFile"
        @hover:row="handleHoverRow"
        @update:sort="handleUpdateSort"
      >
        <Column
          :label="$t('admin.settings.documents.table.columns.title')"
          :isSortable="true"
          field="title"
        >
          <template #cell="{ data }">
            <div
              class="flex w-full flex-row justify-between items-center gap-2"
            >
              <FileType
                size="sm"
                variant="solid"
                :extension="data.file.name.split('.').pop()"
              />
              <div
                class="w-full leading-5 font-medium text-sm truncate flex text-primary"
              >
                <TextWithTooltip
                  :title="data.title"
                  :listeners="[leftWidth, width]"
                  :lines="2"
                />
              </div>
              <div class="flex flex-row gap-0.5 pr-1 shrink-0">
                <Visibility :is-private="!data.public" :icon-only="true" />
              </div>
            </div>
          </template>
        </Column>
      </Table>
    </template>
    <template #center>
      <Table
        :showColumns="columns"
        :data="files"
        :isLoading="isLoading"
        :sortBy="filters.sort_by"
        :orderBy="filters.order_by"
        :hasColumnPicker="true"
        :hoveredRow="hoveredRow"
        rowHeight="56"
        @clicked:row="handleClickFile"
        @hover:row="handleHoverRow"
        @update:sort="handleUpdateSort"
        @update:column-preferences="
          (value) => setPreference('files.grid.columns', value)
        "
      >
        <Column
          :label="$t('admin.settings.documents.table.columns.created_at')"
          :isSortable="true"
          field="created_at"
          size="100"
        >
          <template #cell="{ data }">
            <span class="text-xs font-medium text-tertiary">
              {{ formatDate(data.created_at) }}
            </span>
          </template>
        </Column>
        <Column
          :label="$t('admin.settings.documents.table.columns.expiration_date')"
          :isSortable="true"
          field="expiration_date"
          size="100"
        >
          <template #cell="{ data }">
            <DueDate
              v-if="data.expiration_date"
              :dueDate="data.expiration_date"
              :isOverdue="isExpired(data.expiration_date)"
              size="sm"
            />
            <span v-else class="text-placeholder_subtle text-xs font-medium">
              {{ $t("components.buttons.set_date") }}
            </span>
          </template>
        </Column>
        <Column
          :label="$t('admin.settings.documents.table.columns.reminder_date')"
          :isSortable="true"
          field="reminder_date"
          size="100"
        >
          <template #cell="{ data }">
            <span
              v-if="data.reminder_date"
              class="text-xs font-medium text-tertiary"
            >
              {{ formatDate(data.reminder_date) }}
            </span>
            <span v-else class="text-placeholder_subtle text-xs font-medium">
              {{ $t("components.buttons.set_date") }}
            </span>
          </template>
        </Column>
        <Column
          :label="$t('admin.settings.documents.table.columns.space')"
          field="space"
          size="200"
        >
          <template #cell="{ data }">
            <LinkedTo
              :variant="getVariant(data)"
              :title="
                data.spaces.length > 0
                  ? parseSpaces(data.spaces)
                  : data.tenant?.name
              "
              :text="data.spaces.length > 0 ? data.tenant?.name : ''"
              :imageUrl="data.spaces[0]?.thumbnailUrl"
            />
          </template>
        </Column>
        <Column
          :label="$t('admin.settings.documents.table.columns.size')"
          :isSortable="true"
          field="size"
          size="100"
        >
          <template #cell="{ data }">
            <span class="text-xs font-medium text-tertiary">
              {{ formatBytes(data.file.size) }}
            </span>
          </template>
        </Column>
        <Column
          :label="$t('admin.settings.documents.table.columns.tags')"
          size="250"
          field="tags"
        >
          <template #cell="{ data }">
            <TagsWithTooltip :tags="data.tags" />
          </template>
        </Column>
      </Table>
    </template>
    <template #footer>
      <Pagination
        :total="totalFiles"
        :perPage="perPage"
        :page="page"
        :perPageOptions="[25, 50, 100]"
        @update:perPage="handleUpdatePerPage($event)"
        @update:page="handleUpdatePage($event)"
      >
        <template #actions>
          <FileExportModal :filters="filters" />
        </template>
      </Pagination>
    </template>
  </DataGrid>

  <template v-if="$slots['bulk-actions']">
    <slot name="bulk-actions" :selectedFiles="selectedFiles" />
  </template>
</template>

<script setup>
// Vue utilities
import { computed, inject, onMounted, ref, watch } from "vue";

// Importing utility libraries
import axios from "axios";
import route from "ziggy-js";
import { storeToRefs } from "pinia";

// Import Components
import { Visibility } from "@/SharedComponents/Badges";
import { TextWithTooltip } from "@/SharedComponents/TextWithTooltip";
import { TagsWithTooltip } from "@/SharedComponents/TagsWithTooltip";
import { FileType } from "@/SharedComponents/File";
import { Table, Column, DataGrid } from "@/SharedComponents/DataGrid";
import { DueDate } from "@/SharedComponents/Dates";
import { Pagination } from "@/SharedComponents/Pagination";
import { FileExportModal } from "@/SharedComponents/Modals";
import { LinkedTo } from "@/SharedComponents/LinkedTo";
import { formatBytes } from "@/Utils/size";

// Importing composables
import { useDate } from "@/Composables/useDate";
import { useFilters } from "@/Composables/useFilters";
import { usePreferences } from "@/Composables/usePreferences";
import { useSearch } from "@/Composables/useSearch";
import { useSorting } from "@/Composables/useSorting";
import { useSelectedRows } from "@/Composables/useSelectedRows";
import { useBreakpoints } from "@/Composables/useBreakpoints";

// Utils
import { parseSpaces } from "@/Utils/linked_to";
import { isExpired } from "@/Utils/files";

// Date utility
const { formatDate } = useDate();

// Preferences
const { setPreference, preferences } = usePreferences();

// Sorting
const sortingStore = useSorting("files");
const { handleSortingChange } = sortingStore;
const { sortingValues } = storeToRefs(sortingStore);

// Filters
const globalFilterStore = useFilters("global");
const { filterValues: globalFilterValues } = storeToRefs(globalFilterStore);
const filterStore = useFilters("files");
const { filterValues } = storeToRefs(filterStore);

// Search
const searchStore = useSearch("files");
const { searchValues } = storeToRefs(searchStore);

// Selection
const selectedRowsStore = useSelectedRows("files");
const { selection } = storeToRefs(selectedRowsStore);
const { handleClearSelection } = selectedRowsStore;

// Breakpoints
const { width } = useBreakpoints();

// Emits
const emit = defineEmits([
  "show:detail",
  "update:count",
  "update:fetchingStatus",
]);

// Define Props
const props = defineProps({
  count: { type: Number, default: 0 },
  fetchingStatus: { type: String, required: true },
  isSelectable: { type: Boolean, required: false },
});

// Emitter
const emitter = inject("emitter");

// Refs
const page = ref(1);
const perPage = ref(25);
const files = ref([]);
const totalFiles = ref(0);
const isLoading = ref(false);
const hoveredRow = ref(null);

// Computed Props
const selectedFiles = computed(() =>
  files.value.filter((file) => selection.value.includes(file.id))
);

const state = computed(() => {
  if (props.fetchingStatus === "loading") {
    return false;
  }

  return props.count === 0 || props.fetchingStatus === "error";
});

const columns = computed(() => {
  return preferences.value["files.grid.columns"]
    ? preferences.value["files.grid.columns"]
    : [
        "created_at",
        "expiration_date",
        "reminder_date",
        "space",
        "size",
        "tags",
      ];
});

const leftWidth = computed(() => {
  return preferences.value["files.grid.leftWidth"];
});

const filters = computed(() => ({
  ...sortingValues.value,
  ...searchValues.value,
  ...filterValues.value,
  tenant: globalFilterValues.value.tenant,
  tenant_only: filterValues.value.tenant?.length > 0,
}));

// Functions
const handleClickFile = (file) => {
  emit("show:detail", file);
};

const handleHoverRow = (row) => {
  hoveredRow.value = row;
};

const handleUpdatePerPage = (value) => {
  perPage.value = value;
  handleUpdatePage(1);
};

const handleUpdatePage = (value) => {
  page.value = value;
  handleClearSelection();
  getFiles();
};

const handleUpdateSort = (field, order) => {
  handleSortingChange({ id: field, desc: order === "desc" });
};

// Get Data
async function getFiles() {
  isLoading.value = true;
  files.value = [];

  const parameters = {
    page: page.value,
    ...filters.value,
    per_page: perPage.value,
  };

  emit("update:fetchingStatus", "loading");

  const url = route("api.documents.search");
  await axios
    .post(url, {
      ...parameters,
    })
    .then((response) => {
      files.value = response.data.data;
      totalFiles.value = response.data.meta.total;
      emit("update:count", response.data.meta.total);
      emit("update:fetchingStatus", "loaded");
    })
    .catch((err) => {
      console.log("error occurred", err);
      emit("update:count", 0);
      emit("update:fetchingStatus", "error");
    })
    .finally(() => {
      isLoading.value = false;
    });
}

const getVariant = (data) => {
  if (data.spaces.length === 1) {
    switch (data.spaces[0].type.value) {
      case "asset_group":
        return "asset_group";
      case "asset":
        return "asset";
      default:
        return "space";
    }
  }

  return data.spaces.length > 1 ? "folder" : "building";
};

// Emitters
emitter.on("update-files-data", (e) => {
  files.value = files.value.map((file) => {
    const updatedFile = e.data.find((f) => f.id === file.id);
    return updatedFile ? updatedFile : file;
  });
});

emitter.on("remove-files-data", (e) => {
  files.value = files.value.filter((file) => !e.data.includes(file.id));
  totalFiles.value -= e.data.length;
});

emitter.on("reload-files", () => {
  getFiles();
});

// Watchers
watch(
  filters,
  () => {
    page.value = 1;
    handleClearSelection();
    getFiles();
  },
  { deep: true }
);

// Lifecycle hooks
onMounted(() => {
  getFiles();
});
</script>
